/**
 * EnergyPlus Example Library Types
 *
 * Types for the example file library with rich metadata for search,
 * filtering, and learning guidance.
 */

/**
 * Primary category for example files.
 */
export type ExampleCategory =
  | 'hvac-systems'
  | 'controls'
  | 'envelope'
  | 'lighting'
  | 'renewables'
  | 'refrigeration'
  | 'water-systems'
  | 'advanced'
  | 'tutorials'
  | 'residential'
  | 'commercial';

/**
 * HVAC system types demonstrated in examples.
 */
export type HVACSystemType =
  | 'vav'
  | 'cav'
  | 'vrf'
  | 'doas'
  | 'radiant'
  | 'packaged'
  | 'split-system'
  | 'heat-pump'
  | 'chilled-beam'
  | 'fan-coil'
  | 'ptac'
  | 'pthp'
  | 'unitary'
  | 'evaporative'
  | 'natural-ventilation'
  | 'hybrid-ventilation'
  | 'displacement-ventilation'
  | 'underfloor-air';

/**
 * Plant equipment types demonstrated in examples.
 */
export type PlantEquipmentType =
  | 'chiller'
  | 'boiler'
  | 'cooling-tower'
  | 'heat-exchanger'
  | 'ground-loop'
  | 'water-heater'
  | 'thermal-storage'
  | 'district-heating'
  | 'district-cooling'
  | 'heat-recovery'
  | 'solar-thermal';

/**
 * Building type that the example represents.
 */
export type BuildingType =
  | 'office-small'
  | 'office-medium'
  | 'office-large'
  | 'retail'
  | 'warehouse'
  | 'school'
  | 'hospital'
  | 'hotel'
  | 'apartment'
  | 'single-family'
  | 'laboratory'
  | 'data-center'
  | 'restaurant'
  | 'generic';

/**
 * Complexity/difficulty level for learning.
 */
export type ComplexityLevel = 'beginner' | 'intermediate' | 'advanced' | 'expert';

/**
 * Learning concept categories.
 */
export type LearningConcept =
  | 'geometry-basics'
  | 'zone-definition'
  | 'constructions'
  | 'schedules'
  | 'internal-loads'
  | 'hvac-basics'
  | 'air-loops'
  | 'plant-loops'
  | 'setpoint-managers'
  | 'controls-ems'
  | 'output-variables'
  | 'sizing'
  | 'heat-balance'
  | 'infiltration'
  | 'natural-ventilation'
  | 'daylighting'
  | 'solar-gains'
  | 'ground-coupling'
  | 'demand-response'
  | 'fault-modeling'
  | 'thermal-mass'
  | 'water-systems';

/**
 * Key EnergyPlus object classes showcased in the example.
 */
export interface ObjectsShowcased {
  /** Primary objects being demonstrated */
  primary: string[];
  /** Secondary objects used but not the focus */
  secondary?: string[];
}

/**
 * Learning path step for guided learning.
 */
export interface LearningStep {
  /** Step title */
  title: string;
  /** Step description */
  description: string;
  /** Objects to focus on in this step */
  focusObjects?: string[];
  /** Tips for this step */
  tips?: string[];
}

/**
 * Learning content for an example file.
 */
export interface LearningContent {
  /** What this example demonstrates */
  summary: string;
  /** Prerequisites before tackling this example */
  prerequisites?: string[];
  /** Key concepts taught */
  concepts: LearningConcept[];
  /** Step-by-step learning guidance */
  steps?: LearningStep[];
  /** Common mistakes to avoid */
  commonMistakes?: string[];
  /** Suggested modifications to try */
  tryThis?: string[];
  /** Related examples for further learning */
  relatedExamples?: string[];
  /** External resources (documentation links) */
  resources?: { title: string; url: string }[];
}

/**
 * Climate zone applicability.
 */
export interface ClimateApplicability {
  /** Ideal climate zones for this example */
  ideal?: string[];
  /** Applicable climate zones */
  applicable?: string[];
  /** Climate zones where this example is not recommended */
  notRecommended?: string[];
}

/**
 * Performance metrics from the example.
 */
export interface PerformanceMetrics {
  /** Annual site energy (kWh) */
  annualSiteEnergy?: number;
  /** Energy Use Intensity (kWh/m2/year) */
  eui?: number;
  /** Peak heating load (W) */
  peakHeating?: number;
  /** Peak cooling load (W) */
  peakCooling?: number;
  /** Annual CO2 emissions (kg) */
  annualCO2?: number;
}

/**
 * Model statistics from the example.
 */
export interface ModelStats {
  /** Number of zones */
  zones: number;
  /** Number of surfaces */
  surfaces: number;
  /** Total floor area (m2) */
  floorArea?: number;
  /** Number of HVAC systems */
  hvacSystems?: number;
  /** Number of plant loops */
  plantLoops?: number;
  /** Total object count */
  objectCount?: number;
}

/**
 * Source information for the example.
 */
export interface ExampleSource {
  /** Original source (e.g., 'EnergyPlus', 'DOE', 'ASHRAE') */
  origin: string;
  /** EnergyPlus version this was created for */
  version: string;
  /** Author or organization */
  author?: string;
  /** Last updated date */
  updated?: string;
  /** License information */
  license?: string;
}

/**
 * Complete metadata for an example file.
 */
export interface ExampleFileMetadata {
  /** Unique identifier */
  id: string;
  /** Display name */
  name: string;
  /** Filename (e.g., '5ZoneAirCooled.idf') */
  filename: string;
  /** Brief description */
  description: string;
  /** Primary category */
  category: ExampleCategory;
  /** Building type */
  buildingType: BuildingType;
  /** Complexity level */
  complexity: ComplexityLevel;
  /** HVAC system types demonstrated */
  hvacSystems?: HVACSystemType[];
  /** Plant equipment types demonstrated */
  plantEquipment?: PlantEquipmentType[];
  /** Key EnergyPlus objects showcased */
  objectsShowcased: ObjectsShowcased;
  /** Feature tags for filtering */
  tags: string[];
  /** Climate applicability */
  climate?: ClimateApplicability;
  /** Model statistics */
  stats?: ModelStats;
  /** Performance metrics (if simulation results available) */
  performance?: PerformanceMetrics;
  /** Source/provenance information */
  source: ExampleSource;
  /** Learning content and guidance */
  learning: LearningContent;
  /** URL to the IDF file */
  idfUrl: string;
  /** Recommended weather file */
  weatherFile?: {
    name: string;
    url?: string;
    climateZone?: string;
  };
  /** Thumbnail/preview image URL */
  thumbnail?: string;
  /** Popularity score (0-100) */
  popularity?: number;
}

/**
 * Search query parameters for example files.
 */
export interface ExampleSearchQuery {
  /** Text search (name, description, tags) */
  text?: string;
  /** Filter by category */
  category?: ExampleCategory | ExampleCategory[];
  /** Filter by building type */
  buildingType?: BuildingType | BuildingType[];
  /** Filter by complexity level */
  complexity?: ComplexityLevel | ComplexityLevel[];
  /** Filter by HVAC system type */
  hvacSystem?: HVACSystemType | HVACSystemType[];
  /** Filter by plant equipment type */
  plantEquipment?: PlantEquipmentType | PlantEquipmentType[];
  /** Filter by learning concept */
  concept?: LearningConcept | LearningConcept[];
  /** Filter by specific EnergyPlus object class */
  objectClass?: string | string[];
  /** Filter by tag */
  tag?: string | string[];
  /** Sort order */
  sortBy?: 'name' | 'popularity' | 'complexity' | 'zones';
  /** Sort direction */
  sortOrder?: 'asc' | 'desc';
  /** Maximum results to return */
  limit?: number;
  /** Offset for pagination */
  offset?: number;
}

/**
 * Search result with additional computed fields.
 */
export interface ExampleSearchResult extends ExampleFileMetadata {
  /** Search relevance score (0-1) */
  score?: number;
  /** Matched fields */
  matchedFields?: string[];
}

/**
 * Filter facet counts for dynamic filter updates.
 */
export interface ExampleFilterFacets {
  categories: { category: ExampleCategory; count: number }[];
  buildingTypes: { buildingType: BuildingType; count: number }[];
  complexityLevels: { complexity: ComplexityLevel; count: number }[];
  hvacSystems: { hvacSystem: HVACSystemType; count: number }[];
  plantEquipment: { equipment: PlantEquipmentType; count: number }[];
  concepts: { concept: LearningConcept; count: number }[];
  tags: { tag: string; count: number }[];
}

/**
 * Search result with total count and facets.
 */
export interface ExampleSearchResultWithFacets {
  results: ExampleSearchResult[];
  totalCount: number;
  hasMore: boolean;
  facets: ExampleFilterFacets;
}

/**
 * View modes for the example library.
 */
export type ExampleLibraryViewMode = 'list' | 'grid' | 'learning-path';

/**
 * Tab options for example preview.
 */
export type ExamplePreviewTab = 'overview' | 'learning' | 'objects' | 'code';

/**
 * Category display information.
 */
export const EXAMPLE_CATEGORIES: Record<ExampleCategory, { name: string; description: string; icon: string }> = {
  'hvac-systems': {
    name: 'HVAC Systems',
    description: 'Air handling, distribution, and zone equipment',
    icon: 'wind',
  },
  'controls': {
    name: 'Controls & EMS',
    description: 'Setpoints, schedules, and custom control logic',
    icon: 'sliders',
  },
  'envelope': {
    name: 'Building Envelope',
    description: 'Walls, windows, roofs, and thermal mass',
    icon: 'box',
  },
  'lighting': {
    name: 'Lighting & Daylighting',
    description: 'Electric lighting and daylighting controls',
    icon: 'sun',
  },
  'renewables': {
    name: 'Renewables & Generation',
    description: 'Solar PV, wind, and on-site generation',
    icon: 'zap',
  },
  'refrigeration': {
    name: 'Refrigeration',
    description: 'Commercial refrigeration and walk-in coolers',
    icon: 'thermometer',
  },
  'water-systems': {
    name: 'Water Systems',
    description: 'Domestic hot water and water-side systems',
    icon: 'droplet',
  },
  'advanced': {
    name: 'Advanced Topics',
    description: 'Complex modeling techniques and edge cases',
    icon: 'cpu',
  },
  'tutorials': {
    name: 'Tutorials',
    description: 'Step-by-step learning examples',
    icon: 'book-open',
  },
  'residential': {
    name: 'Residential',
    description: 'Single-family homes and multi-family buildings',
    icon: 'home',
  },
  'commercial': {
    name: 'Commercial',
    description: 'Office buildings, retail, and warehouses',
    icon: 'building',
  },
};

/**
 * Complexity level display information.
 */
export const COMPLEXITY_LEVELS: Record<ComplexityLevel, { name: string; description: string; color: string }> = {
  'beginner': {
    name: 'Beginner',
    description: 'Basic concepts, ideal for new users',
    color: 'green',
  },
  'intermediate': {
    name: 'Intermediate',
    description: 'Moderate complexity, some experience helpful',
    color: 'blue',
  },
  'advanced': {
    name: 'Advanced',
    description: 'Complex systems, requires solid understanding',
    color: 'orange',
  },
  'expert': {
    name: 'Expert',
    description: 'Highly complex, for experienced users',
    color: 'red',
  },
};

/**
 * HVAC system type display information.
 */
export const HVAC_SYSTEM_TYPES: Record<HVACSystemType, { name: string; description: string }> = {
  'vav': { name: 'VAV', description: 'Variable Air Volume systems' },
  'cav': { name: 'CAV', description: 'Constant Air Volume systems' },
  'vrf': { name: 'VRF', description: 'Variable Refrigerant Flow systems' },
  'doas': { name: 'DOAS', description: 'Dedicated Outdoor Air Systems' },
  'radiant': { name: 'Radiant', description: 'Radiant heating/cooling systems' },
  'packaged': { name: 'Packaged', description: 'Packaged rooftop units' },
  'split-system': { name: 'Split System', description: 'Split system heat pumps/AC' },
  'heat-pump': { name: 'Heat Pump', description: 'Heat pump systems' },
  'chilled-beam': { name: 'Chilled Beam', description: 'Chilled beam systems' },
  'fan-coil': { name: 'Fan Coil', description: 'Fan coil units' },
  'ptac': { name: 'PTAC', description: 'Packaged Terminal Air Conditioner' },
  'pthp': { name: 'PTHP', description: 'Packaged Terminal Heat Pump' },
  'unitary': { name: 'Unitary', description: 'Unitary equipment' },
  'evaporative': { name: 'Evaporative', description: 'Evaporative cooling systems' },
  'natural-ventilation': { name: 'Natural Ventilation', description: 'Natural ventilation strategies' },
  'hybrid-ventilation': { name: 'Hybrid Ventilation', description: 'Mixed mode ventilation' },
  'displacement-ventilation': { name: 'Displacement', description: 'Displacement ventilation' },
  'underfloor-air': { name: 'Underfloor Air', description: 'Underfloor air distribution' },
};

/**
 * Learning concept display information.
 */
export const LEARNING_CONCEPTS: Record<LearningConcept, { name: string; description: string }> = {
  'geometry-basics': { name: 'Geometry Basics', description: 'Building geometry and surfaces' },
  'zone-definition': { name: 'Zone Definition', description: 'Thermal zone setup' },
  'constructions': { name: 'Constructions', description: 'Material and construction layers' },
  'schedules': { name: 'Schedules', description: 'Time-based schedules' },
  'internal-loads': { name: 'Internal Loads', description: 'People, lights, equipment' },
  'hvac-basics': { name: 'HVAC Basics', description: 'HVAC fundamentals' },
  'air-loops': { name: 'Air Loops', description: 'Air handling systems' },
  'plant-loops': { name: 'Plant Loops', description: 'Chilled/hot water loops' },
  'setpoint-managers': { name: 'Setpoint Managers', description: 'Temperature and humidity control' },
  'controls-ems': { name: 'Controls & EMS', description: 'Energy Management System' },
  'output-variables': { name: 'Output Variables', description: 'Simulation outputs' },
  'sizing': { name: 'Sizing', description: 'Equipment sizing' },
  'heat-balance': { name: 'Heat Balance', description: 'Heat transfer calculations' },
  'infiltration': { name: 'Infiltration', description: 'Air leakage modeling' },
  'natural-ventilation': { name: 'Natural Ventilation', description: 'Natural ventilation' },
  'daylighting': { name: 'Daylighting', description: 'Daylighting controls' },
  'solar-gains': { name: 'Solar Gains', description: 'Solar heat gain calculations' },
  'ground-coupling': { name: 'Ground Coupling', description: 'Ground heat transfer' },
  'demand-response': { name: 'Demand Response', description: 'Load shifting and DR' },
  'fault-modeling': { name: 'Fault Modeling', description: 'Equipment fault simulation' },
  'thermal-mass': { name: 'Thermal Mass', description: 'Thermal storage in building materials' },
  'water-systems': { name: 'Water Systems', description: 'Domestic hot water and plumbing' },
};
